"use client";

import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useRouter, usePathname } from "next/navigation";
import * as Yup from "yup";
import { IoMdArrowDropdown } from "react-icons/io";
import { FaPaperPlane } from "react-icons/fa";
import Link from "next/link";

import { useAuthContext } from "@/components/providers/AuthContextProvider";
import ButtonLoader from "@/components/shared/ButtonLoader";
import { Typography } from "@/components/shared/Typography";
import { fetchNewsComments, postNewsComment } from "@/services/newsService";
import { ArticleCommentType, ArticleType } from "@/types/article";
import { getProfileImageUrl } from "@/utils/getImageUrl";
import { getTimeAgoShort } from "@/utils/getTimeAgoShort";
import { getLabel } from "@/data/labels";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";
import Image from "next/image";

interface ArticleDetailCommentSectionProps {
  article: ArticleType;
}

const ArticleDetailCommentSection = ({ article }: ArticleDetailCommentSectionProps) => {
  const [comments, setComments] = useState<ArticleCommentType[]>([]);
  const [totalDocs, setTotalDocs] = useState<number>(0);

  const [filters, setFilters] = useState({
    sortBy: "latest",
    limit: 2,
    page: 1,
  });

  const router = useRouter();
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const language = locale?.split("-")[0] || (article?.language === "te" ? "te" : "en");

  const { data, error, isFetching } = useQuery({
    queryKey: ["fetchNewsComments", article?.id, filters?.sortBy, filters?.page],
    queryFn: () =>
      fetchNewsComments({
        newsId: article?.id,
        limit: filters?.limit,
        page: filters?.page,
        sortBy: filters?.sortBy,
      }),
  });

  const handleLoadMore = () => {
    setFilters(prev => ({ ...prev, page: prev.page + 1 }));
  };

  const showLoadMore = data && data.totalDocs > comments.length;

  useEffect(() => {
    if (data) {
      if (filters.page === 1) {
        setComments(data.comments);
      } else {
        setComments(prevComments => [...prevComments, ...data.comments]);
      }
      setTotalDocs(data?.totalDocs || 0);
    }
  }, [data, filters.page]);

  return (
    <div className="w-full comment__container">
      {/* Header with Icon */}

            {/* Separator Lines */}
            <div className="flex flex-col gap-0.5 mb-8 mt-6">
        <div className="h-px bg-gray-300 w-full"></div>
        <div className="h-px bg-gray-300 w-full"></div>
      </div>

      <div className="flex items-center gap-1.5 mb-2">
        
        <div className="relative w-[35px] h-[47px] flex-shrink-0 flex items-center justify-center">
          <Image
            src="/icons/CarouselIcon.svg"
            alt=""
            width={35}
            height={47}
            className="h-full w-full object-contain"
          />
        </div>
        <Typography variant="h4" className="text-lg md:text-xl lg:text-2xl">
          {language === "te" ? "కామెంట్‌లు" : "COMMENTS"} ({totalDocs || 0})
        </Typography>
      </div>
      
      {/* Separator Lines */}
      <div className="flex flex-col gap-0.5 mb-8 mt-6 ">
        <div className="h-px bg-gray-300 w-full"></div>
        <div className="h-px bg-gray-300 w-full"></div>
      </div>

      <CommentInputBox
        newsId={article?.id}
        parentComment={null}
        setComments={setComments}
        setTotalDocs={setTotalDocs}
      />

      <div className="mt-6">
        {comments?.map((comment, index) => {
          return (
            <Comment
              key={index}
              comment={comment}
              setComments={setComments}
              setTotalDocs={setTotalDocs}
            />
          );
        })}
      </div>

      {showLoadMore && (
        <button
          onClick={handleLoadMore}
          disabled={isFetching}
          className="bg-[#4777DE] w-full h-[40px] text-white font-medium text-sm rounded-sm mt-6"
          aria-label={getLabel(language, "LOAD_MORE_COMMENTS")}
        >
          {getLabel(language, "LOAD_MORE_COMMENTS")}
        </button>
      )}
    </div>
  );
};

const addReplyToComments = (
  comments: ArticleCommentType[],
  newReply: ArticleCommentType,
): ArticleCommentType[] => {
  return comments?.map(comment => {
    if (comment?.id === newReply?.parentComment) {
      return {
        ...comment,
        replyComments: [...(comment?.replyComments || []), newReply],
      };
    }

    if (comment?.replyComments && comment?.replyComments?.length) {
      return {
        ...comment,
        replyComments: addReplyToComments(comment.replyComments, newReply),
      };
    }

    return comment;
  });
};

const CommentInputBox = ({
  newsId,
  parentComment,
  setComments,
  setTotalDocs,
  setIsCommentInputOpen,
}: {
  newsId: string;
  parentComment: ArticleCommentType | null;
  setComments: React.Dispatch<React.SetStateAction<ArticleCommentType[]>>;
  setTotalDocs: React.Dispatch<React.SetStateAction<number>>;
  setIsCommentInputOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [formError, setFormError] = useState("");

  const { user } = useAuthContext();
  const router = useRouter();
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const language = locale?.split("-")[0] || "en";

  const mutation = useMutation({
    mutationFn: async (newComment: { newsId: string; comment: string; parentComment?: string }) => {
      try {
        const response = await postNewsComment(newComment);
        return response;
      } catch (error) {
        throw error;
      }
    },
    onSuccess: newComment => {
      if (newComment?.comment?.parentComment) {
        setComments(prevComments => addReplyToComments(prevComments, newComment?.comment));
      } else {
        setComments(prev => [newComment?.comment, ...prev]);
        setTotalDocs(prev => prev + 1);
      }

      formik.resetForm();
      if (setIsCommentInputOpen) setIsCommentInputOpen(false);
    },
    onError: (err: any) => {
      if (err?.status === 401) {
        router.push(`/${locale}/login`);
        return;
      }
      setFormError(err?.message || (language === "te" ? "ఏదో తప్పు జరిగింది, మళ్లీ ప్రయత్నించండి." : "Something went wrong, try again."));
    },
  });

  const formik = useFormik({
    initialValues: {
      comment: "",
    },
    validationSchema: Yup.object({
      comment: Yup.string().trim().required(language === "te" ? "కామెంట్ అవసరం!" : "Comment is required!"),
    }),
    onSubmit: async values => {
      setFormError("");
      const resolvedNewsId =
        newsId && typeof newsId === "object"
          ? (newsId as any)?.value?.id || (newsId as any)?.value || ""
          : newsId;

      mutation.mutate({
        newsId: resolvedNewsId,
        comment: values?.comment,
        parentComment: parentComment?.id,
      });
    },
  });

  return (
    <form
      action=""
      onSubmit={e => {
        e.preventDefault();
        if (!user) {
          router.push(`/${locale}/login`);
          return;
        }
        formik.handleSubmit();
      }}
      className="w-full"
    >
      <div className="relative">
        <input
          type="text"
          name="comment"
          id="comment"
          className={`w-full px-4 pr-14 py-6 h-28 text-sm outline-none rounded-lg bg-gray-100 dark:bg-gray-700 dark:text-white ${
            formik.touched.comment && Boolean(formik.errors.comment)
              ? " border border-red-500 "
              : ""
          }`}
          placeholder={language === "te" ? "కామెంట్ జోడించండి" : "Add Comment"}
          value={formik.values.comment || ""}
          onChange={formik.handleChange}
        />
        <button
          className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-lg flex items-center justify-center hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={mutation.isPending}
          type="submit"
          aria-label="Post comment"
        >
          {mutation.isPending ? (
            <ButtonLoader />
          ) : (
            <FaPaperPlane className="text-blue-500" size={26} />
          )}
        </button>
        {((formik.touched.comment && Boolean(formik.errors.comment)) || formError) && (
          <span className="absolute -bottom-5 left-0 text-xs text-red-500">{formik.errors.comment || formError}</span>
        )}
      </div>
    </form>
  );
};

const CommentTextBlock = ({ text }: { text: string }) => (
  <div className="mt-1 min-w-0">
    <p className="text-sm leading-[1.5] break-words dark:text-white">{text}</p>
  </div>
);

const Comment = ({
  comment,
  setComments,
  setTotalDocs,
}: {
  comment: ArticleCommentType;
  setComments: React.Dispatch<React.SetStateAction<ArticleCommentType[]>>;
  setTotalDocs: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const [isCommentInputOpen, setIsCommentInputOpen] = useState(false);
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const language = locale?.split("-")[0] || "en";

  return (
    <div className="mb-4 last:mb-0">
      <div className="flex items-start gap-3">
        <div className="w-[100px] h-[70px] flex-shrink-0 rounded overflow-hidden">
          <img
            src={(comment?.user?.pictureURL && (getProfileImageUrl(comment.user.pictureURL) || comment.user.pictureURL)) || "/images/avatar.webp"}
            alt=""
            onError={e => {
              e.currentTarget.src = "/images/avatar.webp";
            }}
            className="object-cover w-full h-full rounded"
            loading="lazy"
          />
        </div>

        <div className="min-w-0 flex-1">
          <span className="block font-medium text-[#385898] dark:text-cyan-400 text-[15px]">
            {comment?.user?.fullName}
          </span>
          <CommentTextBlock text={comment?.comment ?? ""} />
          <div className="flex items-center gap-4 mt-2">
            {/* <span>Like</span> */}
            <button
              className="text-sm text-[#4267B2] dark:text-cyan-500 font-[500]"
              onClick={() => setIsCommentInputOpen(!isCommentInputOpen)}
              aria-label="Reply"
            >
              {language === "te" ? "సమాధానం" : "Reply"}
            </button>
            <span className="text-sm text-[#90949C] font-[500]">
              {getTimeAgoShort(comment?.createdAt)}
            </span>
          </div>
        </div>
      </div>
      {isCommentInputOpen && (
        <div className="ml-[52px] mt-4">
          <CommentInputBox
            newsId={comment?.newsId || comment?.contentId || ""}
            parentComment={comment}
            setComments={setComments}
            setTotalDocs={setTotalDocs}
            setIsCommentInputOpen={setIsCommentInputOpen}
          />
        </div>
      )}

      {comment?.replyComments?.length > 0 &&
        comment?.replyComments?.map((replyComment, index) => {
          return (
            <div key={index} className="ml-[52px] border-l border-dotted pl-2 mt-4">
              <Comment
                key={index}
                comment={replyComment}
                setComments={setComments}
                setTotalDocs={setTotalDocs}
              />
            </div>
          );
        })}
    </div>
  );
};

export default ArticleDetailCommentSection;
